import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { usePolling } from '../hooks/useApi';

const SECRET_FIELDS = [
  { key: 'secret_toncenter_api_key', label: 'TON Center API Key', type: 'password' },
  { key: 'secret_ton_wallet_address', label: 'TON Wallet Address', type: 'text' },
  { key: 'secret_ton_wallet_mnemonic', label: 'TON Wallet Mnemonic', type: 'textarea' },
  { key: 'secret_gate_api_key', label: 'Gate.io API Key', type: 'password' },
  { key: 'secret_gate_api_secret', label: 'Gate.io API Secret', type: 'password' },
  { key: 'secret_mexc_api_key', label: 'MEXC API Key', type: 'password' },
  { key: 'secret_mexc_api_secret', label: 'MEXC API Secret', type: 'password' },
  { key: 'secret_binance_api_key', label: 'Binance API Key', type: 'password' },
  { key: 'secret_binance_api_secret', label: 'Binance API Secret', type: 'password' },
  { key: 'secret_bingx_api_key', label: 'BingX API Key', type: 'password' },
  { key: 'secret_bingx_api_secret', label: 'BingX API Secret', type: 'password' },
  { key: 'secret_bitmart_api_key', label: 'BitMart API Key', type: 'password' },
  { key: 'secret_bitmart_api_secret', label: 'BitMart API Secret', type: 'password' },
  { key: 'secret_bitmart_api_memo', label: 'BitMart API Memo', type: 'password' },
  { key: 'secret_weex_api_key', label: 'WEEX API Key', type: 'password' },
  { key: 'secret_weex_api_secret', label: 'WEEX API Secret', type: 'password' },
  { key: 'secret_weex_api_passphrase', label: 'WEEX API Passphrase', type: 'password' },
  { key: 'secret_bybit_api_key', label: 'Bybit API Key', type: 'password' },
  { key: 'secret_bybit_api_secret', label: 'Bybit API Secret', type: 'password' },
  { key: 'secret_telegram_bot_token', label: 'Telegram Bot Token', type: 'password' },
  { key: 'secret_telegram_allowed_user_id', label: 'Telegram Allowed User ID', type: 'text' },
];

export function SettingsPage() {
  const { data: settings, refetch } = usePolling(() => api.getSettings(), 15000);
  const { data: secrets, refetch: refetchSecrets } = usePolling(() => api.getSecrets(), 15000);
  const { data: sysStatus, refetch: refetchStatus } = usePolling(() => api.getSystemStatus(), 10000);
  const [editSettings, setEditSettings] = useState<Record<string, string>>({});
  const [secretForm, setSecretForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [savingSecrets, setSavingSecrets] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (settings) setEditSettings(prev => ({ ...prev, ...settings }));
  }, [settings]);

  const initialized = React.useRef(false);
  useEffect(() => {
    if (secrets && !initialized.current) {
      initialized.current = true;
      const init: Record<string, string> = {};
      for (const [k, v] of Object.entries(secrets)) init[k] = v;
      setSecretForm(init);
    }
  }, [secrets]);

  async function handleSave() {
    setSaving(true); setMessage('');
    try {
      await api.updateSettings(editSettings);
      setMessage('Настройки сохранены');
      refetch();
    } catch (e: any) {
      setMessage('Ошибка: ' + e.message);
    } finally { setSaving(false); }
  }

  async function handleSaveSecrets() {
    setSavingSecrets(true); setMessage('');
    try {
      const formData = { ...secretForm };
      await api.updateSecrets(formData);
      setMessage('Секреты сохранены и применены сразу.');
      refetchSecrets();
    } catch (e: any) {
      setMessage('Ошибка: ' + e.message);
    } finally { setSavingSecrets(false); }
  }

  async function handleKillSwitch() {
    try { await api.killSwitchOn(); refetchStatus(); setMessage('Kill switch активирован'); }
    catch (e: any) { setMessage('Ошибка: ' + e.message); }
  }

  async function handleKillSwitchOff() {
    try { await api.killSwitchOff(); refetchStatus(); setMessage('Kill switch деактивирован'); }
    catch (e: any) { setMessage('Ошибка: ' + e.message); }
  }

  return (
    <div>
      <div className="section-title">Статус системы</div>
      <div className="card">
        {sysStatus && (
          <div className="settings-grid">
            <div className="route-field"><label>Режим</label><span className="text-mono text-sm">{sysStatus.liveEnabled ? 'LIVE' : 'Dry Run'}</span></div>
            <div className="route-field"><label>Сканер</label><span className="text-mono text-sm">{sysStatus.scannerRunning ? 'РАБОТАЕТ' : sysStatus.scannerEnabled ? 'АВТО' : 'ВЫКЛ'}</span></div>
            <div className="route-field"><label>Автобаланс</label><span className="text-mono text-sm">{sysStatus.autoBalanceEnabled ? (sysStatus.autoBalanceExecutionEnabled ? 'БОЕВОЙ' : 'DRY') : 'ВЫКЛ'}</span></div>
            <div className="route-field"><label>Маршрутов</label><span className="text-mono text-sm">{sysStatus.routesCount}</span></div>
            <div className="route-field"><label>Сделок</label><span className="text-mono text-sm">{sysStatus.tradesCount}</span></div>
            <div className="route-field"><label>Макс. тикет</label><span className="text-mono text-sm">${sysStatus.maxTicketUsd || 'N/A'}</span></div>
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <button className="btn btn-sm btn-danger" onClick={handleKillSwitch}>Kill Switch ВКЛ</button>
          <button className="btn btn-sm btn-outline" onClick={handleKillSwitchOff}>Kill Switch ВЫКЛ</button>
        </div>
      </div>

      <div className="section-title mt-2">Секреты (хранятся в БД)</div>
      <div className="card">
        <div className="text-xs text-secondary mb-2">******** — значение уже установлено. Оставьте ******** чтобы не менять. После сохранения применяется сразу.</div>
        <div className="settings-grid">
          {SECRET_FIELDS.map(f => (
            <div key={f.key} className="route-field">
              <label>{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  value={secretForm[f.key] || ''}
                  onChange={e => setSecretForm({ ...secretForm, [f.key]: e.target.value })}
                  rows={2}
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 8px', fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}
                />
              ) : (
                <input type={f.type} value={secretForm[f.key] || ''} onChange={e => setSecretForm({ ...secretForm, [f.key]: e.target.value })} />
              )}
            </div>
          ))}
        </div>
        <button className="btn btn-primary mt-2" onClick={handleSaveSecrets} disabled={savingSecrets}>
          {savingSecrets ? 'Сохранение...' : 'Сохранить секреты'}
        </button>
      </div>

      <div className="section-title mt-2">Глобальные настройки</div>
      <div className="card">
        <div className="settings-grid">
          {[
            { key: 'live_mode', label: 'Live Mode', type: 'select', options: ['true', 'false'] },
            { key: 'kill_switch', label: 'Kill Switch', type: 'select', options: ['true', 'false'] },
            { key: 'scanner_interval_ms', label: 'Интервал сканера (мс)', type: 'number' },
            { key: 'observation_limit', label: 'Лимит наблюдений', type: 'number' },
            { key: 'route_spread_history_retention_days', label: 'История спредов (дней)', type: 'number' },
            { key: 'balance_monitor_interval', label: 'Интервал мониторинга (с)', type: 'number' },
            { key: 'auto_balance_enabled', label: 'Автобаланс planner', type: 'select', options: ['true', 'false'] },
            { key: 'auto_balance_execution_enabled', label: 'Боевой автобаланс', type: 'select', options: ['confirmed', 'disabled'] },
            { key: 'auto_balance_cooldown_sec', label: 'Автобаланс кулдаун (с)', type: 'number' },
            { key: 'auto_balance_balance_max_age_sec', label: 'Свежесть балансов (с)', type: 'number' },
          ].map(f => (
            <div key={f.key} className="route-field">
              <label>{f.label}</label>
              {f.type === 'select' ? (
                <select value={editSettings[f.key] || 'false'} onChange={e => setEditSettings({ ...editSettings, [f.key]: e.target.value })}>
                  {f.options!.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type} value={editSettings[f.key] || ''} onChange={e => setEditSettings({ ...editSettings, [f.key]: e.target.value })} />
              )}
            </div>
          ))}
        </div>
        <button className="btn btn-primary mt-2" onClick={handleSave} disabled={saving}>
          {saving ? 'Сохранение...' : 'Сохранить настройки'}
        </button>
        {message && <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{message}</p>}
      </div>
    </div>
  );
}
